import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { B2BPasswordStrengthCheckResponse } from '@stytch/vanilla-js'

type PasswordStrengthCheckProps = {
  password: string
  setIsStrong: Dispatch<SetStateAction<boolean>>
}

const scores = [1, 2, 3, 4]

export default function PasswordStrengthCheck({
  password,
  setIsStrong,
}: PasswordStrengthCheckProps) {
  const stytch = useStytchB2BClient()
  const [response, setResponse] = useState<B2BPasswordStrengthCheckResponse>()

  const strengthCheck = useCallback(async () => {
    try {
      const passwordResponse = await stytch.passwords.strengthCheck({
        password,
      })
      setResponse(passwordResponse)
      setIsStrong(passwordResponse.score >= 3)
    } catch (error) {
      setResponse(undefined)
      setIsStrong(false)
    }
  }, [password, setIsStrong, stytch.passwords])

  useEffect(() => {
    if (password) {
      strengthCheck()
    }
  }, [password, strengthCheck])

  return (
    // Using important since parent container using margin for spacing
    <Box sx={{ mt: '8px !important' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        {scores.map((score) => {
          const currentScore = response?.score || 1
          return (
            <Box
              key={score}
              sx={{
                flexGrow: 1,
                height: 4,
                borderRadius: '20px',
                transition: 'background-color 300ms ease-in-out',
                backgroundColor:
                  currentScore >= score
                    ? currentScore < 3
                      ? 'error.main'
                      : 'success.main'
                    : 'grey.300',
              }}
            />
          )
        })}
      </Stack>
      {response?.zxcvbn_feedback.warning && (
        <Typography variant="caption" color="error">
          {response.zxcvbn_feedback.warning}
        </Typography>
      )}
    </Box>
  )
}
